import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Group, Stepper, Text } from '@mantine/core';
import { IconBan } from '@tabler/icons-react';
import { useInterval } from '@mantine/hooks';
import * as Enums from '@/utils/enums';
import ImportDraftService, { errorMessage, isApiError, reinferRefusal } from '@/services/import-draft/import-draft-service';
import { ImportDraft, ImportDraftCommitResult, ImportRecipe } from '@/services/import-draft/import-draft-model';
import { WizardStep, cloneRecipe, isCommitted, mapBackToType, isExtracted, isProcessing, stepForDraft, wizardStepper } from '@/services/import-draft/import-draft-helpers';
import UploadStep, { UploadState } from './UploadStep';
import ProcessingCard from './ProcessingCard';
import MappingStep from './MappingStep';
import DataStep from './DataStep';
import ConfirmStep from './ConfirmStep';

interface Props {
  draftID?: string | null;
  onDraftChange?: (draftID: string | null) => void;
}

const POLL_MS = 2000;

const initialUpload: UploadState = {
  importType: Enums.ImportType.Customer,
  file: null,
  instruction: '',
  allowGeneratedCodes: true,
};

export default function SmartImportWizard({ draftID, onDraftChange }: Props) {
  const [upload, setUpload] = useState<UploadState>(initialUpload);
  const [draft, setDraft] = useState<ImportDraft | null>(null);
  const [recipe, setRecipe] = useState<ImportRecipe | null>(null);
  const [dirty, setDirty] = useState(false);
  const [step, setStep] = useState<WizardStep>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportDraftCommitResult | null>(null);
  const [reinferProblem, setReinferProblem] = useState<{ message: string; refused: boolean } | null>(null);
  const [processingSince, setProcessingSince] = useState<number | null>(null);
  const lastStage = useRef<number | null>(null);
  const cancelledIDs = useRef<Set<string>>(new Set());

  const adopt = useCallback((next: ImportDraft, resetRecipe: boolean) => {
    if (cancelledIDs.current.has(next.ID)) return;
    setDraft(next);
    if (resetRecipe || !recipe) {
      setRecipe(next.Recipe ? cloneRecipe(next.Recipe) : null);
      setDirty(false);
    }
    if (lastStage.current !== next.Stage) {
      lastStage.current = next.Stage;
      if (next.Stage === Enums.ImportDraftStage.Ready && step === 0) setStep(stepForDraft(next));
      if (next.Stage === Enums.ImportDraftStage.Committed) setStep(3);
    }
  }, [recipe, step]);

  const refresh = useCallback(async (resetRecipe: boolean) => {
    if (!draft?.ID && !draftID) return;
    const fetched = await ImportDraftService.get(draft?.ID || draftID!);
    if (isApiError(fetched)) {
      setError(errorMessage(fetched, 'The import could not be loaded.'));
      return;
    }
    adopt(fetched as ImportDraft, resetRecipe);
  }, [draft?.ID, draftID, adopt]);

  useEffect(() => {
    if (draftID && !draft) {
      ImportDraftService.get(draftID).then(fetched => {
        if (isApiError(fetched)) setError(errorMessage(fetched, 'The import could not be loaded.'));
        else if (!cancelledIDs.current.has((fetched as ImportDraft).ID)) {
          const loaded = fetched as ImportDraft;
          setDraft(loaded);
          setRecipe(loaded.Recipe ? cloneRecipe(loaded.Recipe) : null);
          lastStage.current = loaded.Stage;
          setStep(stepForDraft(loaded));
        }
      });
    }
  }, [draftID, draft]);

  const polling = !!draft && isProcessing(draft.Stage);
  const interval = useInterval(() => { refresh(true); }, POLL_MS);
  useEffect(() => {
    if (polling) interval.start();
    else interval.stop();
    return interval.stop;
  }, [polling]);

  useEffect(() => {
    if (polling) setProcessingSince(since => since ?? Date.now());
    else setProcessingSince(null);
  }, [polling]);

  async function submitUpload(file: File) {
    setBusy(true);
    setError(null);
    const created = await ImportDraftService.create({
      file,
      importType: upload.importType,
      instruction: upload.instruction,
      allowGeneratedCodes: upload.allowGeneratedCodes,
      mode: Enums.ImportDraftMode.Auto,
    });
    setBusy(false);
    if (isApiError(created)) {
      setError(errorMessage(created, 'The file could not be uploaded.'));
      return;
    }
    const next = created as ImportDraft;
    lastStage.current = next.Stage;
    setDraft(next);
    setRecipe(null);
    setDirty(false);
    setStep(0);
    onDraftChange?.(next.ID);
  }

  async function reinfer(instruction: string, mode: number = Enums.ImportDraftMode.Table) {
    if (!draft) return;
    setBusy(true);
    setError(null);
    setReinferProblem(null);
    const response = await ImportDraftService.reinfer(draft.ID, instruction, upload.allowGeneratedCodes, mode);
    setBusy(false);
    if (isApiError(response)) {
      const refusal = reinferRefusal(response);
      setReinferProblem(refusal
        ? { message: refusal, refused: true }
        : { message: errorMessage(response, 'The file could not be processed again.'), refused: false });
      return;
    }
    lastStage.current = null;
    setStep(0);
    setRecipe(null);
    setDirty(false);
    adopt(response as ImportDraft, true);
    setTimeout(() => refresh(true), 500);
  }

  async function continueFromMapping() {
    if (!draft || !recipe) return;
    if (!dirty) {
      setStep(2);
      return;
    }
    setBusy(true);
    setError(null);
    const saved = await ImportDraftService.updateRecipe(draft.ID, recipe);
    setBusy(false);
    if (isApiError(saved)) {
      setError(errorMessage(saved, 'The mapping could not be saved.'));
      return;
    }
    adopt(saved as ImportDraft, true);
    setStep(2);
  }

  async function commit() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    const response = await ImportDraftService.commit(draft.ID);
    setBusy(false);
    if (isApiError(response)) {
      setError(errorMessage(response, 'The import could not be created.'));
      return;
    }
    setResult(response as ImportDraftCommitResult);
    refresh(true);
  }

  function cancelProcessing() {
    if (!draft) return;
    cancelledIDs.current.add(draft.ID);
    ImportDraftService.cancel(draft.ID);
    setDraft({ ...draft, Stage: Enums.ImportDraftStage.Cancelled });
    setStep(0);
  }

  function undoCategoryMove(columnIndex: number) {
    const base = recipe || draft?.Recipe;
    if (!base) return;
    setRecipe(mapBackToType(cloneRecipe(base), columnIndex));
    setDirty(true);
    setStep(1);
  }

  function redetect() {
    if (!draft) return;
    reinfer(draft.UserInstruction || '', isExtracted(draft) ? Enums.ImportDraftMode.Extract : Enums.ImportDraftMode.Table);
  }

  function startOver() {
    if (draft && draft.Stage !== Enums.ImportDraftStage.Committed && draft.Stage !== Enums.ImportDraftStage.Cancelled) {
      cancelledIDs.current.add(draft.ID);
      ImportDraftService.cancel(draft.ID);
    }
    setDraft(null);
    setRecipe(null);
    setDirty(false);
    setResult(null);
    setError(null);
    setReinferProblem(null);
    setStep(0);
    lastStage.current = null;
    setUpload({ ...initialUpload, importType: upload.importType });
    onDraftChange?.(null);
  }

  const showProcessing = !!draft && step === 0 && (isProcessing(draft.Stage) || draft.Stage === Enums.ImportDraftStage.Failed);
  const cancelled = !!draft && draft.Stage === Enums.ImportDraftStage.Cancelled;
  const steps = wizardStepper(step, step > 0 && !!draft && !isExtracted(draft));
  const activeStep = Math.max(0, steps.findIndex(entry => entry.wizardStep === step));

  return (
    <Card shadow="md" withBorder radius="md" p="lg" style={{ backgroundColor: 'white' }} data-testid="smart-import-wizard">
      <Stepper active={activeStep} size="sm" mb="lg" allowNextStepsSelect={false} onStepClick={index => {
        const target = steps[index];
        if (target && target.wizardStep > 0 && target.wizardStep < step && !isCommitted(draft)) setStep(target.wizardStep);
      }}>
        {steps.map(entry => <Stepper.Step key={entry.wizardStep} label={entry.label} description={entry.description} />)}
      </Stepper>

      {reinferProblem && (
        <Alert
          color={reinferProblem.refused ? 'yellow' : 'red'}
          mb="md"
          withCloseButton
          onClose={() => setReinferProblem(null)}
          data-testid="reinfer-problem"
        >
          <Text size="sm">{reinferProblem.message}</Text>
        </Alert>
      )}

      {step === 0 && !draft && (
        <UploadStep state={upload} onChange={setUpload} onSubmit={submitUpload} submitting={busy} error={error} />
      )}

      {showProcessing && draft && (
        <ProcessingCard
          draft={draft}
          busy={busy}
          onRetry={() => reinfer(draft.UserInstruction || '', draft.Mode === Enums.ImportDraftMode.Extract ? Enums.ImportDraftMode.Extract : Enums.ImportDraftMode.Auto)}
          onReadAsReport={() => reinfer(draft.UserInstruction || '', Enums.ImportDraftMode.Extract)}
          onCancel={draft.Stage === Enums.ImportDraftStage.Failed ? startOver : cancelProcessing}
          startedAt={processingSince}
        />
      )}

      {step === 0 && cancelled && draft && (
        <Alert color="gray" icon={<IconBan size={16} />} title="Import cancelled" data-testid="draft-cancelled">
          <Text size="sm">Processing of {draft.SourceFileName} was stopped. Nothing was saved.</Text>
          <Button mt="sm" size="xs" onClick={startOver}>Upload a file</Button>
        </Alert>
      )}

      {step === 1 && draft && isExtracted(draft) && (
        <Alert color="blue" title="Column mapping not needed" data-testid="extracted-mapping-notice">
          <Text size="sm">{draft.ModeReason || 'This file was processed as a report, so there are no columns to map.'}</Text>
          <Group mt="sm" gap="xs">
            <Button size="xs" onClick={() => setStep(2)}>Review records</Button>
            <Button size="xs" variant="light" loading={busy} onClick={() => reinfer(draft.UserInstruction || '', Enums.ImportDraftMode.Table)}>Process as a table instead</Button>
          </Group>
        </Alert>
      )}

      {step === 1 && draft && !isExtracted(draft) && recipe && (
        <MappingStep
          draft={draft}
          recipe={recipe}
          dirty={dirty}
          busy={busy}
          onRecipeChange={next => { setRecipe(next); setDirty(true); }}
          onContinue={continueFromMapping}
          onReinfer={reinfer}
        />
      )}

      {step === 2 && draft && (
        <DataStep
          draft={draft}
          onBack={() => setStep(1)}
          onContinue={() => setStep(3)}
          onReread={isExtracted(draft) ? (instruction: string) => reinfer(instruction, Enums.ImportDraftMode.Extract) : undefined}
          onRetry={(instruction: string) => reinfer(instruction, Enums.ImportDraftMode.Auto)}
          onRedetect={redetect}
          busy={busy}
        />
      )}

      {step === 3 && draft && (
        <ConfirmStep
          draft={draft}
          recipe={recipe}
          result={result}
          committing={busy}
          error={error}
          onBack={() => setStep(2)}
          onEditMapping={isExtracted(draft) ? undefined : () => setStep(1)}
          onRedetect={redetect}
          onUndoCategoryMove={isExtracted(draft) ? undefined : undoCategoryMove}
          onCommit={commit}
          onStartAnother={startOver}
        />
      )}
    </Card>
  );
}
